import { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Plus, Truck, Wallet, FileText, MessageSquare, AlertTriangle, Calendar,
  ArrowRight, Users, ChevronDown, CheckCircle2, Clock, Sparkles, Loader2,
  RefreshCw, Activity, ShieldCheck,
} from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import DossierForm from "@/components/dossier/DossierForm";
import SpotlightSearch from "@/components/SpotlightSearch";
import { WV, Chip } from "@/design/wyvio";

const fmtWeekday = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("fr-FR", {
      weekday: "long", day: "numeric", month: "long",
    });
  } catch { return ""; }
};

const fmtTime = (iso) => {
  if (!iso) return null;
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
  } catch { return null; }
};

const fmtRelative = (iso) => {
  if (!iso) return "";
  try {
    const then = new Date(iso).getTime();
    const now = Date.now();
    const diff = Math.round((now - then) / 1000);
    if (diff < 60) return "à l'instant";
    if (diff < 3600) return `il y a ${Math.floor(diff / 60)} min`;
    if (diff < 86400) return `il y a ${Math.floor(diff / 3600)}h`;
    return `il y a ${Math.floor(diff / 86400)}j`;
  } catch { return ""; }
};

// ---------- Sub-components ---------------------------------------------

const StatusCard = ({ icon: Icon, label, sublabel, value, tone = "default", onClick, testId }) => {
  // Palette WYVIO stricte : blanc, gris #F7F8FA, noir #111, lime #C8FF2F, chip lime #F0FFDE
  const toneStyles = {
    primary: "bg-[#111111] text-white",
    danger:  "bg-[#F7F8FA] text-[#111111] border border-black/[0.08]",
    warning: "bg-[#F7F8FA] text-[#111111] border border-black/[0.08]",
    info:    "bg-white border border-black/[0.05]",
    default: "bg-white border border-black/[0.05]",
    lime:    "bg-[#C8FF2F] text-[#111111]",
  }[tone];
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-left rounded-2xl p-6 transition-all hover:-translate-y-0.5 hover:shadow-[0_8px_24px_-12px_rgba(17,17,17,0.12)] ${toneStyles}`}
    >
      <div className="flex items-start justify-between">
        <Icon size={18} strokeWidth={1.75} className="opacity-80" />
        <span className="text-[10px] font-semibold uppercase tracking-[0.14em] opacity-70">
          {sublabel}
        </span>
      </div>
      <p className="mt-6 text-[48px] font-semibold tracking-[-0.03em] tabular-nums leading-none">
        {value}
      </p>
      <p className="mt-3 text-[12px] font-semibold uppercase tracking-[0.11em] opacity-70">
        {label}
      </p>
    </button>
  );
};

const DeliveryReadinessCard = ({ ready, blocked, todaysDeliveries }) => {
  // Palette WYVIO stricte : lime pour prêt/OK, ink pour bloqué (typographie + icône, pas la couleur)
  const tone = blocked > 0
    ? { bg: WV.muted,     border: WV.hairline,          head: "Livraisons bloquées",       useIcon: true,  chipBg: WV.ink,      chipFg: "#fff" }
    : ready > 0
    ? { bg: WV.limeChip,  border: "rgba(0,0,0,0.05)",   head: "Livraisons prêtes",         useIcon: false, chipBg: WV.lime,     chipFg: WV.ink }
    : { bg: WV.muted,     border: WV.hairline,          head: "Aucune livraison imminente", useIcon: false, chipBg: WV.muted,   chipFg: WV.textMuted };
  return (
    <div data-testid="delivery-readiness" className="rounded-2xl p-6" style={{ background: tone.bg, border: `1px solid ${tone.border}` }}>
      <div className="flex items-center gap-2">
        {tone.useIcon ? (
          <AlertTriangle size={14} strokeWidth={2.25} style={{ color: WV.ink }} />
        ) : (
          <span className="h-2 w-2 rounded-full animate-pulse" style={{ background: ready > 0 ? WV.limeDot : WV.textFaint }} />
        )}
        <p className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: WV.textMuted }}>
          {tone.head}
        </p>
      </div>
      <div className="mt-5 grid grid-cols-3 gap-4">
        <div>
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em]" style={{ color: WV.textMuted }}>Prêtes</p>
          <p className="mt-2 text-[34px] font-semibold tracking-[-0.03em] tabular-nums leading-none" style={{ color: WV.ink }}>{ready}</p>
        </div>
        <div>
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em]" style={{ color: WV.textMuted }}>Aujourd&apos;hui</p>
          <p className="mt-2 text-[34px] font-semibold tracking-[-0.03em] tabular-nums leading-none" style={{ color: WV.ink }}>{todaysDeliveries}</p>
        </div>
        <div>
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.14em]" style={{ color: WV.textMuted }}>Bloquées</p>
          <p className="mt-2 text-[34px] font-semibold tracking-[-0.03em] tabular-nums leading-none" style={{ color: WV.ink }}>{blocked}</p>
        </div>
      </div>
    </div>
  );
};

const PriorityActionCard = ({ item, onClick }) => {
  // Palette stricte : blocked = card avec icône warning + typo, en attente = chip lime
  const stateMeta = item.action === "blocked"
    ? { label: "Bloqué",     cls: "bg-[#F7F8FA] text-[#111111]", Icon: AlertTriangle }
    : { label: "En attente", cls: "bg-[#F0FFDE] text-[#4a6b0f]", Icon: Clock };
  const Icon = stateMeta.Icon;
  return (
    <article
      data-testid={`priority-action-${item.id}`}
      className="wyvio-card wyvio-card-hover p-5 flex items-center gap-4 group"
    >
      <div className={`h-11 w-11 rounded-2xl flex items-center justify-center shrink-0 ${stateMeta.cls}`}>
        <Icon size={16} strokeWidth={2} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${stateMeta.cls}`}>
            {stateMeta.label}
          </span>
          {item.commercial && (
            <span className="text-[10.5px] font-medium text-[#6B7280]">
              · {item.commercial}
            </span>
          )}
        </div>
        <p className="mt-1 text-[14px] font-semibold text-[#111111] truncate">
          {item.step_label}
        </p>
        <p className="text-[12px] text-[#6B7280] truncate">
          {item.vehicle_name} · {item.client_name || "—"}
        </p>
      </div>
      <button
        onClick={onClick}
        data-testid={`priority-continue-${item.id}`}
        className="h-9 rounded-full bg-[#111111] text-white text-[12px] font-semibold px-4 inline-flex items-center gap-1 hover:brightness-110 shrink-0"
      >
        Continuer
        <ArrowRight size={12} strokeWidth={2.5} />
      </button>
    </article>
  );
};

const TeamMemberCard = ({ member, active, onClick }) => (
  <button
    data-testid={`team-member-${member.id}`}
    onClick={onClick}
    className={[
      "text-left rounded-3xl p-5 border transition-all",
      active
        ? "bg-[#111111] text-white border-[#111111]"
        : "bg-white border-[#EEF2F5] hover:border-[#111111]/40 hover:shadow-sm",
    ].join(" ")}
  >
    <div className="flex items-center gap-3">
      <div className={`h-11 w-11 rounded-2xl flex items-center justify-center text-[13px] font-semibold ${
        active ? "bg-[#C8FF2F] text-[#111111]" : "bg-[#111111] text-white"
      }`}>
        {(member.name || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase()}
      </div>
      <div className="min-w-0 flex-1">
        <p className={`text-[14px] font-semibold tracking-tight truncate ${active ? "text-white" : "text-[#111111]"}`}>
          {member.name}
        </p>
        <p className={`text-[11.5px] truncate ${active ? "text-white/70" : "text-[#6B7280]"}`}>
          {member.role === "admin" ? "Administrateur" : "Commercial"}
        </p>
      </div>
    </div>
    <ul className={`mt-4 space-y-1.5 text-[12.5px] ${active ? "text-white/90" : "text-[#374151]"}`}>
      {member.counts.deliveries_today > 0 && (
        <li className="flex items-center gap-2">
          <Truck size={12} strokeWidth={2} /> {member.counts.deliveries_today} livraison{member.counts.deliveries_today > 1 ? "s" : ""} aujourd&apos;hui
        </li>
      )}
      {member.counts.blocked > 0 && (
        <li className="flex items-center gap-2">
          <AlertTriangle size={12} strokeWidth={2} /> {member.counts.blocked} dossier{member.counts.blocked > 1 ? "s" : ""} bloqué{member.counts.blocked > 1 ? "s" : ""}
        </li>
      )}
      {member.counts.documents_waiting > 0 && (
        <li className="flex items-center gap-2">
          <FileText size={12} strokeWidth={2} /> {member.counts.documents_waiting} document{member.counts.documents_waiting > 1 ? "s" : ""} attendu{member.counts.documents_waiting > 1 ? "s" : ""}
        </li>
      )}
      {member.counts.messages > 0 && (
        <li className="flex items-center gap-2">
          <MessageSquare size={12} strokeWidth={2} /> {member.counts.messages} message{member.counts.messages > 1 ? "s" : ""} client
        </li>
      )}
      {member.counts.deliveries_today === 0 &&
        member.counts.blocked === 0 &&
        member.counts.documents_waiting === 0 &&
        member.counts.messages === 0 && (
        <li className="flex items-center gap-2 opacity-70">
          <CheckCircle2 size={12} strokeWidth={2} /> Aucune action urgente
        </li>
      )}
    </ul>
    <p className={`mt-4 text-[11px] ${active ? "text-white/60" : "text-[#9CA3AF]"}`}>
      {member.counts.active_dossiers} dossier{member.counts.active_dossiers > 1 ? "s" : ""} actif{member.counts.active_dossiers > 1 ? "s" : ""}
    </p>
  </button>
);

const RecentActivityItem = ({ ev, onClick }) => {
  const map = {
    step_status_changed: "Étape mise à jour",
    step_blocked:        "Étape bloquée",
    step_unblocked:      "Étape débloquée",
    step_completed:      "Étape terminée",
    delivery_unlocked:   "Livraison débloquée",
    document_added:      "Document ajouté",
    document_viewed_by_client: "Document consulté (client)",
    message_sent:        "Message envoyé",
    dossier_created:     "Dossier créé",
    dossier_finished:    "Dossier terminé",
    media_added:         "Média ajouté",
    cpi_added:           "CPI ajouté",
  };
  return (
    <button
      onClick={onClick}
      data-testid={`activity-${ev.id}`}
      className="w-full flex items-start gap-3 py-2.5 text-left hover:bg-[#F7F8FA] rounded-xl px-2 -mx-2 transition-colors"
    >
      <span className="mt-1 h-1.5 w-1.5 rounded-full bg-[#C8FF2F] shrink-0" />
      <div className="min-w-0 flex-1">
        <p className="text-[12.5px] text-[#111111] leading-snug">
          <span className="font-semibold">{map[ev.type] || ev.type}</span>
          {ev.meta?.step_label ? <span> · {ev.meta.step_label}</span> : null}
        </p>
        <p className="text-[11px] text-[#6B7280]">
          {ev.actor_name || "Système"} · {fmtRelative(ev.timestamp)}
        </p>
      </div>
    </button>
  );
};

// ---------- Main Page --------------------------------------------------

const Dashboard = () => {
  const navigate = useNavigate();
  const { agency, user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");
  const [showForm, setShowForm] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    try {
      const s = await api.getDashboardSummary(filter === "all" ? null : filter);
      setSummary(s);
      setError(null);
    } catch (e) {
      setError(e.message || "Erreur de chargement");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  useEffect(() => { setLoading(true); load(); }, [filter]);

  const handleRefresh = () => { setRefreshing(true); load(); };

  const agencyName = summary?.agency?.name || agency?.name || "l'agence";

  if (loading && !summary) {
    return (
      <div className="wyvio-fade-in p-8" data-testid="page-dashboard">
        <div className="animate-pulse space-y-4">
          <div className="h-8 w-64 bg-[#EEF2F5] rounded" />
          <div className="h-4 w-96 bg-[#EEF2F5] rounded" />
          <div className="grid grid-cols-4 gap-4 mt-8">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="h-28 bg-[#EEF2F5] rounded-3xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error && !summary) {
    return (
      <div className="p-8" data-testid="page-dashboard">
        <p className="text-[13px] text-[#991B1B]">{error}</p>
        <button onClick={load} className="mt-4 h-9 rounded-full bg-[#111111] text-white text-[12.5px] px-4">
          Réessayer
        </button>
      </div>
    );
  }

  const t = summary.totals;

  return (
    <div className="wyvio-fade-in" data-testid="page-dashboard">
      {/* HEADER : agency welcome + filter + refresh */}
      <header className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
        <div>
          <Chip>
            <span className="capitalize">{fmtWeekday(summary.today)}</span>
          </Chip>
          <h1
            data-testid="dashboard-title"
            className="mt-5 text-[38px] sm:text-[48px] lg:text-[56px] leading-[1.03] font-semibold tracking-[-0.03em]"
            style={{ color: WV.ink }}
          >
            Bienvenue chez {agencyName}.
          </h1>
          <p className="mt-4 text-[14.5px] max-w-xl leading-relaxed" style={{ color: WV.textMuted }}>
            {t.active_dossiers} dossier{t.active_dossiers > 1 ? "s" : ""} actif{t.active_dossiers > 1 ? "s" : ""} · {t.blocked} bloqué{t.blocked > 1 ? "s" : ""} · {t.ready_for_delivery} prêt{t.ready_for_delivery > 1 ? "s" : ""} pour la livraison.
          </p>
        </div>
        <div className="flex items-center gap-2 self-start lg:self-auto">
          <select
            data-testid="team-filter"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="h-11 rounded-full border bg-white px-4 text-[13px] font-medium outline-none focus:border-[#111111]"
            style={{ borderColor: WV.hairline, color: WV.ink }}
          >
            <option value="all">Agence entière</option>
            {(summary.team || []).map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          <button
            onClick={handleRefresh}
            data-testid="dashboard-refresh"
            title="Rafraîchir"
            className={`h-11 w-11 rounded-full border bg-white flex items-center justify-center transition-colors hover:bg-[#F7F8FA] ${refreshing ? "animate-spin-slow" : ""}`}
            style={{ borderColor: WV.hairline, color: WV.textMuted }}
          >
            <RefreshCw size={14} strokeWidth={2} />
          </button>
          <button
            data-testid="dashboard-new-btn"
            onClick={() => setShowForm(true)}
            className="group inline-flex items-center gap-2 h-11 px-5 rounded-full bg-[#C8FF2F] text-[#111111] text-[13px] font-semibold hover:brightness-95 active:scale-[0.98] transition-all"
          >
            <Plus size={14} strokeWidth={2.5} />
            Nouveau dossier
          </button>
        </div>
      </header>

      {/* SEARCH */}
      <section className="mt-6" data-testid="search-section">
        <SpotlightSearch />
      </section>

      {/* GLOBAL STATUS - 4 KPI cards */}
      <section
        data-testid="global-status"
        className="mt-6 grid grid-cols-2 lg:grid-cols-4 gap-3"
      >
        <StatusCard icon={Truck} label="Livraisons aujourd'hui" sublabel="Aujourd'hui"
          value={t.todays_deliveries} tone="lime"
          onClick={() => navigate("/app/planning")}
          testId="status-todays-deliveries" />
        <StatusCard icon={Wallet} label="Paiements en attente" sublabel="À encaisser"
          value={t.waiting_payments} tone={t.waiting_payments > 0 ? "warning" : "default"}
          testId="status-waiting-payments" />
        <StatusCard icon={FileText} label="Documents attendus" sublabel="Workflow"
          value={t.documents_waiting} tone="default"
          testId="status-documents-waiting" />
        <StatusCard icon={MessageSquare} label="Messages non lus" sublabel="Clients"
          value={t.unread_messages} tone={t.unread_messages > 0 ? "info" : "default"}
          testId="status-unread-messages" />
      </section>

      {/* DELIVERY READINESS + UPCOMING */}
      <section className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1">
          <DeliveryReadinessCard
            ready={t.ready_for_delivery}
            blocked={t.blocked}
            todaysDeliveries={t.todays_deliveries}
          />
        </div>

        {/* Blocked files WHY */}
        <div className="lg:col-span-2 rounded-2xl border bg-white p-6" style={{ borderColor: WV.hairline }} data-testid="blocked-files-widget">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: WV.textMuted }}>
                Dossiers bloqués
              </p>
              <h3 className="mt-2 text-[17px] font-semibold tracking-tight" style={{ color: WV.ink }}>
                Ce qui vous empêche d&apos;avancer
              </h3>
            </div>
            <Link
              to="/app/dossiers"
              className="text-[11.5px] font-medium hover:opacity-70"
              style={{ color: WV.textMuted }}
            >
              Tous les dossiers →
            </Link>
          </div>
          {(summary.blocked_files || []).length === 0 ? (
            <div className="mt-6 text-center py-10 text-[13px]" style={{ color: WV.textMuted }}>
              <div className="mx-auto h-10 w-10 flex items-center justify-center rounded-full" style={{ background: WV.limeChip }}>
                <ShieldCheck size={18} strokeWidth={1.8} style={{ color: WV.limeChipTx }} />
              </div>
              <p className="mt-3">Aucun dossier bloqué. Bon travail !</p>
            </div>
          ) : (
            <ul className="mt-4 space-y-2">
              {summary.blocked_files.slice(0, 5).map((b) => (
                <li
                  key={b.id}
                  data-testid={`blocked-file-${b.id}`}
                  className="flex items-start gap-3 rounded-2xl p-4"
                  style={{ background: WV.muted, border: `1px solid ${WV.hairline}` }}
                >
                  <div className="h-9 w-9 rounded-xl flex items-center justify-center shrink-0" style={{ background: WV.ink, color: "#fff" }}>
                    <AlertTriangle size={14} strokeWidth={2} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13.5px] font-semibold truncate" style={{ color: WV.ink }}>
                      {b.vehicle_name} · {b.client_name || "—"}
                    </p>
                    <p className="mt-0.5 text-[12px] truncate" style={{ color: WV.textMuted }}>
                      En attente : {b.reasons.map((r) => r.step_label).join(", ")}
                    </p>
                  </div>
                  <button
                    onClick={() => navigate(`/app/dossier/${b.id}`)}
                    className="h-8 rounded-full text-white text-[11.5px] font-semibold px-3 shrink-0 hover:brightness-110"
                    style={{ background: WV.ink }}
                  >
                    Ouvrir
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* PRIORITY ACTIONS + TEAM (2-col) */}
      <section className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Priority actions */}
        <div className="lg:col-span-2" data-testid="priority-actions">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-[19px] font-semibold tracking-tight text-[#111111]">
                Mes actions prioritaires
              </h2>
              <p className="mt-0.5 text-[12.5px] text-[#6B7280]">
                Générées par le workflow · classées par urgence.
              </p>
            </div>
            {summary.priority_actions.length > 0 && (
              <span className="inline-flex items-center gap-1.5 rounded-full bg-[#111111] text-white px-2.5 py-1 text-[11px] font-semibold">
                <Sparkles size={11} strokeWidth={2.25} />
                {summary.priority_actions.length}
              </span>
            )}
          </div>
          {summary.priority_actions.length === 0 ? (
            <div className="rounded-2xl border border-dashed p-10 text-center" style={{ borderColor: WV.hairline }}>
              <div className="mx-auto h-11 w-11 flex items-center justify-center rounded-full" style={{ background: WV.limeChip }}>
                <CheckCircle2 size={20} strokeWidth={1.8} style={{ color: WV.limeChipTx }} />
              </div>
              <p className="mt-4 text-[14px] font-semibold" style={{ color: WV.ink }}>Tout est à jour</p>
              <p className="mt-1 text-[12.5px]" style={{ color: WV.textMuted }}>
                Aucune action prioritaire aujourd&apos;hui.
              </p>
            </div>
          ) : (
            <div className="space-y-2.5">
              {summary.priority_actions.map((it) => (
                <PriorityActionCard
                  key={it.id + it.step_key}
                  item={it}
                  onClick={() => navigate(`/app/dossier/${it.id}`)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Team overview */}
        <div data-testid="team-overview">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-[19px] font-semibold tracking-tight text-[#111111]">
                Vue agence
              </h2>
              <p className="mt-0.5 text-[12.5px] text-[#6B7280]">
                Charge de travail par collaborateur.
              </p>
            </div>
            <Users size={16} strokeWidth={1.75} className="text-[#6B7280]" />
          </div>
          <div className="space-y-3">
            {summary.team.map((m) => (
              <TeamMemberCard
                key={m.id}
                member={m}
                active={filter === m.id}
                onClick={() => setFilter(filter === m.id ? "all" : m.id)}
              />
            ))}
          </div>
        </div>
      </section>

      {/* PLANNING preview + RECENT ACTIVITY */}
      <section className="mt-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 rounded-2xl border bg-white p-6" style={{ borderColor: WV.hairline }} data-testid="upcoming-widget">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: WV.textMuted }}>
                À venir
              </p>
              <h3 className="mt-2 text-[17px] font-semibold tracking-tight" style={{ color: WV.ink }}>
                Livraisons des 7 prochains jours
              </h3>
            </div>
            <Link
              to="/app/planning"
              className="inline-flex items-center gap-1 text-[11.5px] font-semibold hover:opacity-70"
              style={{ color: WV.ink }}
              data-testid="dashboard-planning-link"
            >
              Voir le planning
              <ArrowRight size={11} strokeWidth={2.5} />
            </Link>
          </div>
          {summary.upcoming_deliveries.length === 0 ? (
            <div className="text-center py-10 text-[13px]" style={{ color: WV.textMuted }}>
              Aucune livraison prévue dans les 7 prochains jours.
            </div>
          ) : (
            <ul className="space-y-2">
              {summary.upcoming_deliveries.slice(0, 6).map((d) => (
                <li key={d.id}
                    className="flex items-center gap-3 rounded-2xl border bg-white p-3 hover:brightness-95 transition-all cursor-pointer"
                    style={{ borderColor: WV.hairline }}
                    onClick={() => navigate(`/app/dossier/${d.id}`)}
                    data-testid={`upcoming-${d.id}`}>
                  <div className="h-11 w-11 rounded-2xl flex flex-col items-center justify-center shrink-0" style={{ background: WV.muted, color: WV.ink }}>
                    <span className="text-[9.5px] font-medium">J+</span>
                    <span className="text-[16px] font-semibold leading-none tracking-tight">{d.days_ahead}</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13.5px] font-semibold truncate" style={{ color: WV.ink }}>
                      {d.vehicle_name} · {d.client_name || "—"}
                    </p>
                    <p className="text-[11.5px] truncate" style={{ color: WV.textMuted }}>
                      {d.commercial ? `${d.commercial} · ` : ""}
                      {d.delivery_gate?.blocked
                        ? <span style={{ color: WV.ink, fontWeight: 600 }}>Bloquée : {d.delivery_gate.blocked_by?.map((b) => b.label).join(", ")}</span>
                        : <span style={{ color: WV.limeChipTx, fontWeight: 600 }}>Prête à livrer</span>}
                    </p>
                  </div>
                  <ArrowRight size={13} strokeWidth={2} style={{ color: WV.textFaint }} />
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border bg-white p-6" style={{ borderColor: WV.hairline }} data-testid="recent-activity">
          <div className="flex items-center justify-between mb-4">
            <div>
              <p className="text-[11px] font-semibold uppercase tracking-[0.14em]" style={{ color: WV.textMuted }}>
                Journal
              </p>
              <h3 className="mt-2 text-[17px] font-semibold tracking-tight" style={{ color: WV.ink }}>
                Activité récente
              </h3>
            </div>
            <Activity size={16} strokeWidth={1.75} style={{ color: WV.textMuted }} />
          </div>
          {summary.recent_activity.length === 0 ? (
            <p className="text-center py-6 text-[12.5px]" style={{ color: WV.textMuted }}>
              Aucune activité récente.
            </p>
          ) : (
            <div className="divide-y max-h-[400px] overflow-y-auto" style={{ borderColor: WV.hairline }}>
              {summary.recent_activity.slice(0, 10).map((ev) => (
                <RecentActivityItem
                  key={ev.id}
                  ev={ev}
                  onClick={() => ev.dossier_id && navigate(`/app/dossier/${ev.dossier_id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </section>

      <div className="h-20" />

      {showForm && (
        <DossierForm
          mode="create"
          onClose={() => setShowForm(false)}
          onSaved={(created) => {
            if (created?.id) navigate(`/app/dossier/${created.id}`);
            else load();
          }}
        />
      )}
    </div>
  );
};

export default Dashboard;
